import { randomUuid } from '../chunk-WVLHT3DL.js';
import '../chunk-RLIE6P2P.js';
import '../chunk-GZ5ZHH3W.js';
import '../chunk-XR6TNFQL.js';
import { unwrapData } from '../chunk-HIWBYL4O.js';
import '../chunk-OCEENQIA.js';
import '../chunk-RMLZ5HCT.js';
import '../chunk-BRBMP7SU.js';
import '../chunk-MZH5OJVQ.js';
import '../chunk-5WIUKZF6.js';
import '../chunk-HCZGW64N.js';
import '../chunk-DCK4H5YA.js';
import '../chunk-PIHMOTST.js';
import '../chunk-BFUJOKOR.js';
import '../chunk-EE4CQDCZ.js';
import '../chunk-6B5KO6TH.js';
import '../chunk-3QN2VZ56.js';
import '../chunk-LNDNXOJP.js';
import '../chunk-LVCDLPYA.js';
import '../chunk-UEXNJL5R.js';
import '../chunk-AEYYQ7QM.js';
import '../chunk-B6XTEEPM.js';
import '../chunk-O2TUCS6S.js';
import '../chunk-RVVFBIJA.js';
import '../chunk-IMD7EVVL.js';
import '../chunk-X3O5L6NE.js';
import '../chunk-GPJ572YS.js';
import { globalSingleton } from '../chunk-N7SZ66QO.js';
import '../chunk-LD6PO66H.js';
import '../chunk-AHISJLEK.js';
import '../chunk-QXYP3KHC.js';
import '../chunk-O5TCTRWM.js';
import '../chunk-O3OIBNP7.js';
import '../chunk-PZ5AY32C.js';

// src/quality/index.ts
var qualityTerminal = (status) => ["succeeded", "completed_with_errors", "cancelled", "failed"].includes(status);
var record = (value) => value && typeof value === "object" && !Array.isArray(value) ? value : void 0;
var text = (value) => typeof value === "string" && value ? value : void 0;
function assessmentFromBody(body) {
  const root = record(body);
  for (const extras of [root?.extras, record(root?.canonical_transaction)?.extras, record(root?.canonical)?.extras]) {
    const saved = record(record(extras)?.tx_quality);
    if (saved) return saved;
  }
  return void 0;
}
var QUALITY_STATES = ["gaps", "not_assessable", "names_missing", "unverified", "not_assessed", "complete"];
var QUALITY_STATE_LABEL = {
  complete: "Complete",
  gaps: "Gaps",
  not_assessable: "Not assessable",
  not_assessed: "Not assessed",
  names_missing: "Names missing",
  unverified: "Unverified"
};
function qualityReviewOf(summary) {
  return summary?.summary?.review ?? summary?.review;
}
function qualityStateOf(summary) {
  if (!summary || !summary.policy_result && !summary.completeness) return "not_assessed";
  const review = qualityReviewOf(summary);
  if (summary.completeness === "partial" || summary.policy_result === "fail" || (review?.check_failures ?? 0) > 0 || review?.status === "gaps" && !review.names_only) return "gaps";
  if (summary.policy_result !== "pass" || summary.completeness !== "complete_for_observed_scope") return "not_assessable";
  if (review?.names_only || (review?.parameters_unnamed ?? 0) > 0) return "names_missing";
  if (review?.status === "unverified" || (review?.unavailable_checks ?? 0) > 0 || review?.unverified_dimensions?.length) return "unverified";
  return "complete";
}
function qualityCompletenessLabel(summary) {
  if (summary?.completeness !== "complete_for_observed_scope") return (summary?.completeness ?? "unknown").replaceAll("_", " ");
  switch (qualityStateOf(summary)) {
    case "names_missing":
      return "Decoded with missing names";
    case "unverified":
      return "Decoded; verification incomplete";
    case "gaps":
      return "Review gaps remain";
    case "not_assessable":
      return "Not assessable";
    default:
      return "Complete for observed scope";
  }
}
function qualityStateDetail(summary) {
  if (!summary || qualityStateOf(summary) === "not_assessed") return "No assessment saved with this row";
  const words = (value) => (value ?? "unknown").replaceAll("_", " ");
  const gaps = Number(record(summary.counts)?.gaps);
  const review = qualityReviewOf(summary);
  return [
    `Policy ${words(summary.policy_result)}`,
    qualityCompletenessLabel(summary).toLowerCase(),
    Number.isFinite(gaps) && gaps > 0 ? `${gaps} gap${gaps === 1 ? "" : "s"}` : void 0,
    (review?.unavailable_checks ?? 0) > 0 ? `${review.unavailable_checks} unavailable checks` : void 0,
    summary.evaluator_version ? `evaluator ${summary.evaluator_version}` : void 0,
    summary.caller_check ? "from a check you ran, not saved with the row" : void 0
  ].filter(Boolean).join(" \xB7 ");
}
var CALLER_QUALITY_BATCH = 50;
var HIT_TTL_MS = 6e4;
var MISS_TTL_MS = 15e3;
var BATCH_WINDOW_MS = 25;
var FAILURE_BACKOFF_MS = 1e4;
function createCallerQualityLoader(client) {
  const cache = /* @__PURE__ */ new Map();
  let pending = /* @__PURE__ */ new Map();
  const flying = /* @__PURE__ */ new Map();
  let timer;
  let revision = 0;
  let quietUntil = 0;
  const listeners = /* @__PURE__ */ new Set();
  const flush = async () => {
    timer = void 0;
    const batch = [...pending.entries()];
    pending = /* @__PURE__ */ new Map();
    const asked = revision;
    for (const [key, p] of batch) flying.set(key, p.answer);
    for (let start = 0; start < batch.length; start += CALLER_QUALITY_BATCH) {
      const group = batch.slice(start, start + CALLER_QUALITY_BATCH);
      const byScope = /* @__PURE__ */ new Map();
      for (const entry of group) byScope.set(entry[1].scope ?? "", [...byScope.get(entry[1].scope ?? "") ?? [], entry]);
      for (const [scope, entries] of byScope) {
        let found;
        try {
          found = await qualitySource(client).summaries([...new Set(entries.map(([, p]) => p.id))], scope || void 0);
        } catch {
          found = void 0;
          quietUntil = Date.now() + FAILURE_BACKOFF_MS;
        }
        for (const [key, p] of entries) {
          const hit = found?.[p.id];
          const value = hit?.summary ? { ...hit.summary, caller_check: true } : void 0;
          if (found && asked === revision) cache.set(key, { at: Date.now(), value });
          if (flying.get(key) === p.answer) flying.delete(key);
          p.resolve(value);
        }
      }
    }
  };
  return {
    load(row, scope) {
      const source = record(row);
      const id = text(source?.canonical_tx_id) ?? text(source?.id);
      if (!id || summaryFromRow(row)) return Promise.resolve(void 0);
      const decode = text(source?.body_etag) ?? text(source?.s3_etag) ?? text(source?.body_key) ?? text(source?.s3_key);
      const key = `${scope ?? ""}|${id}|${decode ?? ""}`;
      const cached = cache.get(key);
      if (cached && Date.now() - cached.at < (cached.value ? HIT_TTL_MS : MISS_TTL_MS)) return Promise.resolve(cached.value);
      const waiting = pending.get(key)?.answer ?? flying.get(key);
      if (waiting) return waiting;
      if (Date.now() < quietUntil) return Promise.resolve(void 0);
      let resolve;
      const answer = new Promise((done) => {
        resolve = done;
      });
      pending.set(key, { id, scope, answer, resolve });
      timer ??= setTimeout(() => {
        void flush();
      }, BATCH_WINDOW_MS);
      return answer;
    },
    clear() {
      cache.clear();
      flying.clear();
      quietUntil = 0;
      revision += 1;
      listeners.forEach((listener) => listener());
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getRevision: () => revision
  };
}
var LOADERS = globalSingleton("quality/callerLoaders", () => /* @__PURE__ */ new WeakMap());
function callerQualityLoader(client) {
  let loader = LOADERS.get(client);
  if (!loader) {
    loader = createCallerQualityLoader(client);
    LOADERS.set(client, loader);
  }
  return loader;
}
function qualityRollup(summaries) {
  const counts = { complete: 0, gaps: 0, not_assessable: 0, not_assessed: 0, names_missing: 0, unverified: 0 };
  for (const summary of summaries) counts[qualityStateOf(summary)] += 1;
  return { total: summaries.length, counts, weakest: QUALITY_STATES.find((state) => counts[state] > 0) ?? null };
}
function summaryFromRow(row) {
  const source = record(row);
  const saved = record(source?.quality_summary);
  if (saved && Object.keys(saved).length) return saved;
  const policy_result = text(source?.quality_status), completeness = text(source?.quality_completeness);
  if (!policy_result && !completeness) return void 0;
  return { policy_result, completeness, evaluator_version: text(source?.quality_evaluator_version), policy_hash: text(source?.quality_policy_hash) };
}
var base = () => "/quality-runs";
function qualitySource(client) {
  return {
    async submit(request, idempotencyKey = randomUuid()) {
      return unwrapData(await client.post(base(), { ...request, idempotency_key: idempotencyKey })).run;
    },
    async list(cursor) {
      return unwrapData(await client.get(base(), { query: cursor ? { cursor } : {}, cacheTtlMs: 0 }));
    },
    async get(id) {
      return unwrapData(await client.get(`${base()}/${encodeURIComponent(id)}`, { cacheTtlMs: 0 })).run;
    },
    async results(id, cursor) {
      return unwrapData(await client.get(`${base()}/${encodeURIComponent(id)}/results`, { query: cursor ? { cursor } : {}, cacheTtlMs: 0 }));
    },
    /**
     * One member's result. `include: ['candidate']` also returns the re-decoded
     * envelope of a re-decode run — what the decode now SAYS, which the
     * assessment (what was read) cannot show, e.g. parameters that gained names.
     */
    async result(id, index, include) {
      return unwrapData(await client.get(
        `${base()}/${encodeURIComponent(id)}/results/${index}`,
        { cacheTtlMs: 0, ...include?.length ? { query: { include: include.join(",") } } : {} }
      ));
    },
    async action(id, action) {
      return unwrapData(await client.post(`${base()}/${encodeURIComponent(id)}/${action}`, {})).run;
    },
    async transaction(id, scope) {
      return unwrapData(await client.get(`/transactions/${encodeURIComponent(id)}/quality`, { query: scope ? { scope } : {}, cacheTtlMs: 0 }));
    },
    /**
     * The caller's own saved summaries for a page of rows, in one request (the
     * batch form of the route above): only checks of the decode each row holds now.
     */
    async summaries(transactionIds, scope) {
      const ids = transactionIds.join(",");
      return unwrapData(await client.get(
        "/transactions/batch/quality",
        { query: scope ? { ids, scope } : { ids }, cacheTtlMs: 0 }
      )).summaries ?? {};
    },
    async save(name, request, runId) {
      return unwrapData(await client.post("/saved-views", { name, table_id: "transaction-quality", scope: "user", view_state: { quality: { schema_version: 1, request, last_run_id: runId } } }));
    },
    async views() {
      return unwrapData(await client.get("/saved-views", { query: { table_id: "transaction-quality" }, cacheTtlMs: 0 }));
    }
  };
}

export { CALLER_QUALITY_BATCH, QUALITY_STATES, QUALITY_STATE_LABEL, assessmentFromBody, callerQualityLoader, createCallerQualityLoader, qualityCompletenessLabel, qualityReviewOf, qualityRollup, qualitySource, qualityStateDetail, qualityStateOf, qualityTerminal, summaryFromRow };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map